/**
 * Pager and LoadMore — the bottom of every list.
 *
 * Two shapes, one sentence (`pagerSummary`):
 *
 *   Pager    — numbered pages over an offset API.
 *     <Pager page={page} pageSize={50} pageCount={rows.length} total={data.total}
 *            onPageChange={setPage} noun="matters" />
 *     → "Showing 51–100 of 132 matters"   (total known)
 *     → "Showing 51–100 matters"           (no total; Next enabled while a page is full)
 *
 *   LoadMore — append over a cursor or offset API (`useInfiniteQuery`).
 *     <LoadMore shown={rows.length} total={total} hasMore={q.hasNextPage}
 *               onLoadMore={() => q.fetchNextPage()} isLoading={q.isFetchingNextPage} />
 *     → "Showing 50 of 132" / "Showing 50"
 *
 * Never pass a `total` you computed from the rows you have — that is the
 * "counter that lies" the 2026-10-03 review found on a dozen pages.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from './Button';
import { cn } from './cn';
import { IconButton } from './IconButton';
import { pagerSummary } from './pagerSummary';

export interface PagerProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Rows on the current page. */
  pageCount: number;
  /** Server total, if the endpoint returns one. */
  total?: number | null;
  /** Server's has-more flag. When absent: from `total`, else "this page is full". */
  hasMore?: boolean;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
  noun?: string;
  className?: string;
}

export function Pager({
  page,
  pageSize,
  pageCount,
  total,
  hasMore,
  onPageChange,
  isLoading = false,
  noun,
  className,
}: PagerProps) {
  const from = (page - 1) * pageSize + 1;
  const knownTotal = typeof total === 'number';
  const hasNext =
    hasMore ?? (knownTotal ? page * pageSize < (total as number) : pageCount >= pageSize);
  const hasPrev = page > 1;
  if (!hasPrev && !hasNext && pageCount === 0) return null;
  return (
    <nav
      aria-label="Pagination"
      className={cn('flex items-center justify-between gap-2 px-1 py-3', className)}
    >
      <p aria-live="polite" className="text-sm tabular-nums text-fg-muted">
        {pagerSummary({ shown: pageCount, total, from: pageCount > 0 ? from : undefined, noun })}
      </p>
      {hasPrev || hasNext ? (
        <div className="flex items-center gap-1">
          <IconButton
            aria-label="Previous page"
            disabled={!hasPrev || isLoading}
            icon={ChevronLeft}
            size="sm"
            variant="secondary"
            onClick={() => onPageChange(Math.max(1, page - 1))}
          />
          <IconButton
            aria-label="Next page"
            disabled={!hasNext || isLoading}
            icon={ChevronRight}
            size="sm"
            variant="secondary"
            onClick={() => onPageChange(page + 1)}
          />
        </div>
      ) : null}
    </nav>
  );
}

export interface LoadMoreProps {
  /** Rows loaded so far. */
  shown: number;
  total?: number | null;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoading?: boolean;
  noun?: string;
  label?: string;
  className?: string;
}

export function LoadMore({
  shown,
  total,
  hasMore,
  onLoadMore,
  isLoading = false,
  noun,
  label = 'Load more',
  className,
}: LoadMoreProps) {
  if (shown === 0 && !hasMore) return null;
  return (
    <div className={cn('flex flex-col items-center gap-2 py-3', className)}>
      <p aria-live="polite" className="text-sm tabular-nums text-fg-muted">
        {pagerSummary({ shown, total, noun })}
      </p>
      {hasMore ? (
        <Button isLoading={isLoading} size="sm" variant="secondary" onClick={onLoadMore}>
          {label}
        </Button>
      ) : null}
    </div>
  );
}
